import { roundStock } from '@/utils/formatting';
import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Database schema
interface OfflineDB extends DBSchema {
  pendingTransactions: {
    key: string;
    value: PendingTransaction;
    indexes: {
      'by-businessId': string;
      'by-status': string;
      'by-timestamp': number;
      'by-type': string;
    };
  };
  products: {
    key: string;
    value: CachedProduct;
    indexes: {
      'by-businessId': string;
      'by-business-location': [string, string];
      'by-sku': string;
      'by-lastSynced': number;
    };
  };
  syncSessions: {
    key: string;
    value: SyncSession;
    indexes: { 'by-businessId': string };
  };
  conflicts: {
    key: string;
    value: ConflictRecord;
    indexes: {
      'by-businessId': string;
      'by-status': string;
      'by-entityType': string;
    };
  };
  settings: {
    key: string;
    value: StorageSettings | any;
  };
  taxGroups: {
    key: string;
    value: CachedTaxGroup;
    indexes: { 'by-businessId': string };
  };
  customers: {
    key: string;
    value: CachedCustomer;
    indexes: {
      'by-businessId': string;
      'by-name': string;
    };
  };
}

export interface PendingTransaction {
  id: string;
  businessId: string;
  transactionData: any;
  timestamp: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  errorMessage?: string;
  retryCount: number;
  type: 'SALE' | 'PRODUCT' | 'INVENTORY' | 'CUSTOMER';
  version: number;
  lastAttempt?: number;
  priority: 'HIGH' | 'NORMAL' | 'LOW';
}

export interface CachedProduct {
  id: string;
  businessId: string;
  locationId: string;
  productId: string;
  data: any;
  lastSynced: number;
  version: number;
  localChanges: boolean;
  sku: string;
  lastUpdated: number;
}

export interface CachedTaxGroup {
  id: string;
  businessId: string;
  data: TaxGroupCacheData;
  lastSynced: number;
}

export interface TaxGroupCacheData {
  groups: Array<{
    id: string;
    name: string;
    description?: string | null;
    isDefault: boolean;
    taxes: Array<{
      id: string;
      name: string;
      rate: number;
      description?: string | null;
    }>;
  }>;
}

export interface CachedCustomer {
  id: string;
  businessId: string;
  data: any;
  lastSynced: number;
  name: string;
  email: string;
  phone: string;
}

export interface SyncSession {
  id: string;
  businessId: string;
  lastSyncAt: number | null;
  syncInProgress: boolean;
  conflictCount: number;
  resolvedConflicts: number;
  failedTransactions: number;
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR';
  errorMessage?: string;
  syncDuration?: number;
  itemsProcessed?: number;
}

export interface ConflictRecord {
  id: string;
  businessId: string;
  entityType: 'PRODUCT' | 'SALE' | 'INVENTORY' | 'CUSTOMER';
  entityId: string;
  localData: any;
  remoteData: any;
  conflictType:
    | 'CREATE'
    | 'UPDATE'
    | 'DELETE'
    | 'VERSION_MISMATCH'
    | 'RETRY_LIMIT_EXCEEDED';
  resolution: 'LOCAL_WINS' | 'REMOTE_WINS' | 'MANUAL_MERGE' | 'PENDING';
  timestamp: number;
  status: 'PENDING' | 'RESOLVED' | 'IGNORED';
  resolvedAt?: number;
  resolvedBy?: string;
}


export interface CachedCashRegisterSession {
  id: string;
  registerId?: string;
  name: string;
  status: 'OPENED' | 'CLOSED';
  openingBalance: number;
  openedAt?: string | null;
  notes?: string | null;
  businessId?: string;
  locationId?: string | null;
  isOfflineSession?: boolean;
}

export interface StorageSettings {
  id: string;
  maxRetryCount: number;
  syncBatchSize: number;
  cacheTTL: number;
  autoCleanup: boolean;
  backupEnabled: boolean;
  lastBackupAt?: number;
}

interface PerformanceMetrics {
  operations: number;
  errors: number;
  lastOperationTime: number;
  slowOperations: number;
  totalOperationTime: number;
}

class OfflineStorageService {
  private db: IDBPDatabase<OfflineDB> | null = null;
  private dbName = 'pos-offline-db';
  private dbVersion = 5;
  private isInitializing = false;
  private initPromise: Promise<void> | null = null;
  private performance: PerformanceMetrics = {
    operations: 0,
    errors: 0,
    lastOperationTime: 0,
    slowOperations: 0,
    totalOperationTime: 0
  };

  // Cached settings to avoid infinite recursion
  private cachedSettings: StorageSettings | null = null;
  private settingsCacheTime: number = 0;
  private readonly SETTINGS_CACHE_TTL = 5000; // 5 seconds

  // Default settings
  private defaultSettings: StorageSettings = {
    id: 'app-settings',
    maxRetryCount: 3,
    syncBatchSize: 50,
    cacheTTL: 24 * 60 * 60 * 1000, // 24 hours
    autoCleanup: true,
    backupEnabled: true
  };

  constructor() {
    // Setup error boundary
    this.setupErrorHandling();
  }

  // ============ INITIALIZATION & CONNECTION MANAGEMENT ============

  async initialize(): Promise<void> {
    if (this.isInitializing && this.initPromise) {
      return this.initPromise;
    }

    if (this.db) return;

    this.isInitializing = true;
    this.initPromise = this._initialize();

    try {
      await this.initPromise;
    } catch (error) {
      this.db = null;
      this.isInitializing = false;
      this.initPromise = null;
      throw error;
    } finally {
      this.isInitializing = false;
      this.initPromise = null;
    }
  }

  private async _initialize(): Promise<void> {
    try {
      this.db = await openDB<OfflineDB>(this.dbName, this.dbVersion, {
        upgrade: (db, oldVersion, newVersion, transaction) => {
          this.handleDatabaseUpgrade(db, oldVersion || 0, newVersion || this.dbVersion, transaction as any);
        },
        blocked: () => {
          //blocked by other tabs
        },
        blocking: () => {
          // this is blocking other tabs
          this.closeConnection();
        },
        terminated: () => {
          //database connection terminated unexpectedly
          this.closeConnection();
        }
      });

      // Add event listeners
      this.db.addEventListener('versionchange', () => {
        this.closeConnection();
      });

      // Initialize default settings
      await this.initializeSettings();

    } catch (error) {
      this.closeConnection();
      throw error;
    }
  }

  private handleDatabaseUpgrade(
    db: IDBPDatabase<OfflineDB>,
    oldVersion: number,
    newVersion: number,
    transaction: IDBTransaction
  ): void {

    // Version 0 -> 1: Initial schema
    if (oldVersion < 1) {
      // Pending Transactions store
      const txStore = db.createObjectStore('pendingTransactions', {
        keyPath: 'id'
      });
      txStore.createIndex('by-businessId', 'businessId');
      txStore.createIndex('by-status', 'status');
      txStore.createIndex('by-timestamp', 'timestamp');
      txStore.createIndex('by-type', 'type');

      // Products cache store
      const productStore = db.createObjectStore('products', {
        keyPath: 'id'
      });
      productStore.createIndex('by-businessId', 'businessId');
      productStore.createIndex('by-business-location', ['businessId', 'locationId']);
      productStore.createIndex('by-sku', 'sku');

      // Sync Sessions store
      const sessionStore = db.createObjectStore('syncSessions', {
        keyPath: 'id'
      });
      sessionStore.createIndex('by-businessId', 'businessId');

      // Conflicts store
      const conflictStore = db.createObjectStore('conflicts', {
        keyPath: 'id'
      });
      conflictStore.createIndex('by-businessId', 'businessId');
      conflictStore.createIndex('by-status', 'status');
      conflictStore.createIndex('by-entityType', 'entityType');

      // Settings store
      db.createObjectStore('settings', {
        keyPath: 'id'
      });
    }

    // Version 1 -> 2: Add new indexes and fields
    if (oldVersion < 2) {
      const productStore = transaction.objectStore('products');
      if (!productStore.indexNames.contains('by-lastSynced')) {
        productStore.createIndex('by-lastSynced', 'lastSynced');
      }

      const pendingStore = transaction.objectStore('pendingTransactions');
      if (!pendingStore.indexNames.contains('by-type')) {
        pendingStore.createIndex('by-type', 'type');
      }
    }

    // Version 2 -> 3: Ensure settings store exists (fixes NotFoundError)
    if (oldVersion < 3) {
      if (!db.objectStoreNames.contains('settings' as any)) {
        db.createObjectStore('settings', {
          keyPath: 'id'
        });
      }

      // Also ensure other critical stores exist if they were missed
      if (!db.objectStoreNames.contains('products' as any)) {
        const productStore = db.createObjectStore('products', { keyPath: 'id' });
        productStore.createIndex('by-businessId', 'businessId');
        productStore.createIndex('by-business-location', ['businessId', 'locationId']);
        productStore.createIndex('by-sku', 'sku');
        productStore.createIndex('by-lastSynced', 'lastSynced');
      }
    }

    // Version 3 -> 4: POS product cache becomes location-scoped.
    // Old business-only rows cannot be trusted for branch stock, so clear them.
    if (oldVersion < 4) {
      const productStore = transaction.objectStore('products');
      if (!productStore.indexNames.contains('by-business-location')) {
        productStore.createIndex('by-business-location', ['businessId', 'locationId']);
      }
      productStore.clear();
    }

    // Version 4 -> 5: Add taxGroups and customers offline caches.
    if (oldVersion < 5) {
      if (!db.objectStoreNames.contains('taxGroups' as any)) {
        const taxStore = db.createObjectStore('taxGroups', { keyPath: 'id' });
        taxStore.createIndex('by-businessId', 'businessId');
      }
      if (!db.objectStoreNames.contains('customers' as any)) {
        const customerStore = db.createObjectStore('customers', { keyPath: 'id' });
        customerStore.createIndex('by-businessId', 'businessId');
        customerStore.createIndex('by-name', 'name');
      }
    }
  }

  private async ensureDb(): Promise<IDBPDatabase<OfflineDB>> {
    if (!this.db) {
      await this.initialize();
    }
    if (!this.db) {
      throw new Error('Database not available after initialization');
    }
    return this.db;
  }

  private closeConnection(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  // ============ ERROR HANDLING & RETRY LOGIC ============

  private setupErrorHandling(): void {
    // Global error handler for uncaught database errors
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        if (event.error?.name?.includes('QuotaExceeded')) {
          this.handleQuotaExceeded().catch(console.error);
        }
      });
    }
  }

  private async withRetry<T>(
    operation: () => Promise<T>,
    operationName: string,
    maxRetries?: number
  ): Promise<T> {
    // Get settings directly without using withRetry to avoid infinite recursion
    const settings = await this.getSettingsDirect();
    const retryCount = maxRetries ?? settings.maxRetryCount;
    let lastError: Error = new Error('Operation failed');

    for (let attempt = 1; attempt <= retryCount; attempt++) {
      try {
        return await this.trackOperation(operationName, operation);
      } catch (error) {
        lastError = error as Error;

        // Don't retry on these errors
        if (this.isFatalError(error)) {
          throw error;
        }

        if (attempt < retryCount) {
         
          // Exponential backoff
          const delay = Math.min(1000, 100 * Math.pow(2, attempt - 1));
          await new Promise(resolve => setTimeout(resolve, delay));

          // Reset connection on certain errors
          if (this.shouldResetConnection(error)) {
            this.closeConnection();
            await this.initialize();
          }
        }
      }
    }

    this.performance.errors++;
    throw lastError!;
  }

  private isFatalError(error: any): boolean {
    const fatalErrors = [
      'ConstraintError',
      'DataError',
      'InvalidAccessError',
      'SyntaxError'
    ];
    return fatalErrors.includes(error?.name);
  }

  private shouldResetConnection(error: any): boolean {
    const resetErrors = [
      'InvalidStateError',
      'UnknownError',
      'AbortError'
    ];
    return resetErrors.includes(error?.name);
  }

  private async handleQuotaExceeded(): Promise<void> {
    try {
      // Delete old completed transactions
      const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const db = await this.ensureDb();

      const tx = db.transaction('pendingTransactions', 'readwrite');
      const store = tx.objectStore('pendingTransactions');
      const index = store.index('by-timestamp');

      const oldTransactions = await index.getAll(IDBKeyRange.upperBound(weekAgo));
      const completed = oldTransactions.filter(t =>
        t.status === 'COMPLETED' || t.status === 'FAILED'
      );

      await Promise.all(completed.map(t => store.delete(t.id)));
      await tx.done;
    } catch (error) {
    }
  }

  // ============ PERFORMANCE MONITORING ============

  private async trackOperation<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    const startTime = performance.now();
    this.performance.operations++;

    try {
      const result = await fn();
      const duration = performance.now() - startTime;

      this.performance.lastOperationTime = duration;
      this.performance.totalOperationTime += duration;

      // Track slow operations
      if (duration > 1000) {
        this.performance.slowOperations++;
      }

      return result;
    } catch (error) {
      this.performance.errors++;
      throw error;
    }
  }

  getPerformanceMetrics(): PerformanceMetrics {
    return { ...this.performance };
  }

  resetPerformanceMetrics(): void {
    this.performance = {
      operations: 0,
      errors: 0,
      lastOperationTime: 0,
      slowOperations: 0,
      totalOperationTime: 0
    };
  }

  // ============ DATA VALIDATION ============

  private readonly validationSchemas = {
    pendingTransaction: {
      id: 'string',
      businessId: 'string',
      transactionData: 'object',
      timestamp: 'number',
      status: ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'],
      retryCount: 'number',
      type: ['SALE', 'PRODUCT', 'INVENTORY', 'CUSTOMER'],
      version: 'number',
      priority: ['HIGH', 'NORMAL', 'LOW']
    } as const,

    cachedProduct: {
      id: 'string',
      businessId: 'string',
      locationId: 'string',
      productId: 'string',
      data: 'object',
      lastSynced: 'number',
      version: 'number',
      localChanges: 'boolean',
      sku: 'string',
      lastUpdated: 'number'
    } as const
  };

  private validateData(schema: any, data: any, context: string): void {
    for (const [key, expectedType] of Object.entries(schema)) {
      if (!(key in data)) {
        throw new Error(`[${context}] Missing required field: ${key}`);
      }

      if (Array.isArray(expectedType)) {
        // Enum validation
        if (!expectedType.includes(data[key])) {
          throw new Error(`[${context}] Invalid value for ${key}: ${data[key]}. Expected one of: ${expectedType.join(', ')}`);
        }
      } else if (typeof data[key] !== expectedType) {
        throw new Error(`[${context}] Invalid type for ${key}: expected ${expectedType}, got ${typeof data[key]}`);
      }
    }

    // Additional business logic validation
    this.validateBusinessLogic(data, context);
  }

  private validateBusinessLogic(data: any, context: string): void {
    if (context === 'pendingTransaction') {
      if (data.retryCount < 0) {
        throw new Error('retryCount cannot be negative');
      }
      if (data.timestamp > Date.now()) {
        throw new Error('timestamp cannot be in the future');
      }
      if (data.version < 1) {
        throw new Error('version must be at least 1');
      }
    }

    if (context === 'cachedProduct') {
      if (!data.locationId || data.locationId.trim() === '') {
        throw new Error('locationId is required and cannot be empty');
      }
      if (!data.productId || data.productId.trim() === '') {
        throw new Error('productId is required and cannot be empty');
      }
      if (!data.sku || data.sku.trim() === '') {
        throw new Error('SKU is required and cannot be empty');
      }
      if (data.lastSynced > Date.now()) {
        throw new Error('lastSynced cannot be in the future');
      }
    }
  }

  // ============ SETTINGS MANAGEMENT ============

  private async initializeSettings(): Promise<void> {
    try {
      const db = await this.ensureDb();
      const existing = await db.get('settings', this.defaultSettings.id);

      if (!existing) {
        await db.put('settings', this.defaultSettings);
      }
    } catch (error) {
    }
  }

  // Direct settings access without retry to avoid infinite recursion
  private async getSettingsDirect(): Promise<StorageSettings> {
    // Use cached settings if available and fresh
    const now = Date.now();
    if (this.cachedSettings && (now - this.settingsCacheTime) < this.SETTINGS_CACHE_TTL) {
      return this.cachedSettings;
    }

    try {
      const db = await this.ensureDb();
      const settings = await db.get('settings', this.defaultSettings.id);
      const result = settings || this.defaultSettings;

      // Cache the settings
      this.cachedSettings = result;
      this.settingsCacheTime = now;

      return result;
    } catch (error) {
      // Return defaults if database access fails
      return this.defaultSettings;
    }
  }

  async getSettings(): Promise<StorageSettings> {
    return this.getSettingsDirect();
  }

  async updateSettings(updates: Partial<StorageSettings>): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const current = await this.getSettingsDirect(); // Use direct access
      const updated = { ...current, ...updates };

      await db.put('settings', updated);

      // Update cache
      this.cachedSettings = updated;
      this.settingsCacheTime = Date.now();
    }, 'updateSettings');
  }

  // ============ PENDING TRANSACTIONS METHODS ============

  async addPendingTransaction(transaction: PendingTransaction): Promise<void> {
    this.validateData(this.validationSchemas.pendingTransaction, transaction, 'pendingTransaction');

    return this.withRetry(async () => {
      const db = await this.ensureDb();
      await db.put('pendingTransactions', {
        ...transaction,
        timestamp: transaction.timestamp || Date.now(),
        version: transaction.version || 1,
        priority: transaction.priority || 'NORMAL'
      });
    }, 'addPendingTransaction');
  }

  async getPendingTransactions(
    businessId: string,
    options: {
      status?: string;
      type?: string;
      limit?: number;
      since?: number;
    } = {}
  ): Promise<PendingTransaction[]> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('pendingTransactions', 'readonly');
      const store = tx.objectStore('pendingTransactions');
      const index = store.index('by-businessId');

      let transactions = await index.getAll(businessId);

      // Apply filters
      if (options.status) {
        transactions = transactions.filter(t => t.status === options.status);
      }
      if (options.type) {
        transactions = transactions.filter(t => t.type === options.type);
      }
      if (options.since) {
        transactions = transactions.filter(t => t.timestamp >= options.since!);
      }

      // Sort and limit
      transactions = transactions.sort((a, b) => {
        // Priority first, then timestamp
        const priorityOrder = { HIGH: 0, NORMAL: 1, LOW: 2 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        return a.timestamp - b.timestamp;
      });

      if (options.limit) {
        transactions = transactions.slice(0, options.limit);
      }

      return transactions;
    }, 'getPendingTransactions');
  }

  async updatePendingTransaction(id: string, updates: Partial<PendingTransaction>): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('pendingTransactions', 'readwrite');
      const store = tx.objectStore('pendingTransactions');
      const existing = await store.get(id);

      if (!existing) {
        throw new Error(`Transaction ${id} not found`);
      }

      // Validate the merged object
      const updated = { ...existing, ...updates, lastAttempt: Date.now() };
      this.validateData(this.validationSchemas.pendingTransaction, updated, 'pendingTransaction');

      await store.put(updated);
      await tx.done;
    }, 'updatePendingTransaction');
  }

  async deletePendingTransaction(id: string): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      await db.delete('pendingTransactions', id);
    }, 'deletePendingTransaction');
  }

  async clearCompletedTransactions(businessId?: string): Promise<number> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('pendingTransactions', 'readwrite');
      const store = tx.objectStore('pendingTransactions');

      let transactions: PendingTransaction[];

      if (businessId) {
        const index = store.index('by-businessId');
        transactions = await index.getAll(businessId);
      } else {
        transactions = await store.getAll();
      }

      const completed = transactions.filter(t => t.status === 'COMPLETED');
      await Promise.all(completed.map(t => store.delete(t.id)));
      await tx.done;

      return completed.length;
    }, 'clearCompletedTransactions');
  }

  // ============ PRODUCTS CACHE METHODS ============

  async cacheProduct(product: CachedProduct): Promise<void> {
    this.validateData(this.validationSchemas.cachedProduct, product, 'cachedProduct');

    return this.withRetry(async () => {
      const db = await this.ensureDb();
      await db.put('products', {
        ...product,
        lastUpdated: Date.now()
      });
    }, 'cacheProduct');
  }

  async getCachedProducts(businessId: string, locationId: string): Promise<CachedProduct[]> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('products', 'readonly');
      const store = tx.objectStore('products');
      const index = store.index('by-business-location');

      return await index.getAll([businessId, locationId]);
    }, 'getCachedProducts');
  }

  async getCachedProduct(id: string): Promise<CachedProduct | undefined> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      return await db.get('products', id);
    }, 'getCachedProduct');
  }

  async updateCachedProduct(id: string, updates: Partial<CachedProduct>): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('products', 'readwrite');
      const store = tx.objectStore('products');
      const existing = await store.get(id);

      if (existing) {
        const updated = { ...existing, ...updates, lastUpdated: Date.now() };
        this.validateData(this.validationSchemas.cachedProduct, updated, 'cachedProduct');
        await store.put(updated);
      }
      await tx.done;
    }, 'updateCachedProduct');
  }

  /**
   * Optimistic local stock after an offline sale is queued.
   * Keeps catalogue/search stock in line until sync + server refetch reconciles.
   */
  async applyOfflineSaleStockAdjustment(
    businessId: string,
    locationId: string,
    items: Array<{ productId: string; quantity: number }>
  ): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('products', 'readwrite');
      const store = tx.objectStore('products');

      for (const { productId, quantity } of items) {
        const qty = Number(quantity);
        if (!productId || Number.isNaN(qty) || qty <= 0) continue;

        const cacheId = this.getProductCacheId(businessId, locationId, productId);
        const existing = await store.get(cacheId);
        if (!existing || existing.businessId !== businessId || existing.locationId !== locationId) continue;

        const current = Number(existing.data?.currentStock ?? 0);
        const next = current - qty;
        const updated: CachedProduct = {
          ...existing,
          data: {
            ...existing.data,
            currentStock: next,
          },
          localChanges: true,
          lastUpdated: Date.now(),
        };
        this.validateData(this.validationSchemas.cachedProduct, updated, 'cachedProduct');
        await store.put(updated);
      }

      await tx.done;
    }, 'applyOfflineSaleStockAdjustment');
  }

  async clearProductCache(businessId?: string, locationId?: string): Promise<number> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('products', 'readwrite');
      const store = tx.objectStore('products');

      let products: CachedProduct[];

      if (businessId && locationId) {
        const index = store.index('by-business-location');
        products = await index.getAll([businessId, locationId]);
        await Promise.all(products.map(p => store.delete(p.id)));
      } else if (businessId) {
        const index = store.index('by-businessId');
        products = await index.getAll(businessId);
        await Promise.all(products.map(p => store.delete(p.id)));
      } else {
        products = await store.getAll();
        await store.clear();
      }

      await tx.done;
      return products.length;
    }, 'clearProductCache');
  }

  async searchCachedProducts(businessId: string, locationId: string, query: string): Promise<any[]> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('products', 'readonly');
      const store = tx.objectStore('products');
      const index = store.index('by-business-location');

      // Get all products for this business/location.
      const allProducts = await index.getAll([businessId, locationId]);

      if (!query || query.length < 2) {
        return [];
      }

      const queryLower = query.toLowerCase();

      // Filter products by name, SKU, or barcode
      const matches = allProducts
        .map(cached => cached.data)
        .filter((product: any) => {
          const name = (product.name || '').toLowerCase();
          const sku = (product.sku || '').toLowerCase();
          const barcode = (product.barcode || '').toLowerCase();

          return name.includes(queryLower) ||
            sku.includes(queryLower) ||
            barcode.includes(queryLower);
        })
        .slice(0, 50); // Limit to 50 results

      // Sort results to prioritize exact matches (same logic as backend)
      return matches.sort((a: any, b: any) => {
        const aNameLower = (a.name || '').toLowerCase();
        const bNameLower = (b.name || '').toLowerCase();
        const aSkuLower = (a.sku || '').toLowerCase();
        const bSkuLower = (b.sku || '').toLowerCase();

        // Exact SKU match gets highest priority
        if (aSkuLower === queryLower && bSkuLower !== queryLower) return -1;
        if (bSkuLower === queryLower && aSkuLower !== queryLower) return 1;

        // Exact name match gets second priority
        if (aNameLower === queryLower && bNameLower !== queryLower) return -1;
        if (bNameLower === queryLower && aNameLower !== queryLower) return 1;

        // Name starts with query gets third priority
        if (aNameLower.startsWith(queryLower) && !bNameLower.startsWith(queryLower)) return -1;
        if (bNameLower.startsWith(queryLower) && !aNameLower.startsWith(queryLower)) return 1;

        // SKU starts with query gets fourth priority
        if (aSkuLower.startsWith(queryLower) && !bSkuLower.startsWith(queryLower)) return -1;
        if (bSkuLower.startsWith(queryLower) && !aSkuLower.startsWith(queryLower)) return 1;

        // Otherwise, alphabetical by name
        return aNameLower.localeCompare(bNameLower);
      });
    }, 'searchCachedProducts');
  }

  async cacheProducts(products: any[], businessId: string, locationId: string): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('products', 'readwrite');
      const store = tx.objectStore('products');

      const now = Date.now();

      for (const product of products) {
        const productId = String(product.id || '');
        if (!productId) continue;
        const cachedProduct: CachedProduct = {
          id: this.getProductCacheId(businessId, locationId, productId),
          businessId,
          locationId,
          productId,
          data: product,
          lastSynced: now,
          version: 1,
          localChanges: false,
          sku: product.sku || '',
          lastUpdated: now
        };

        await store.put(cachedProduct);
      }

      await tx.done;
    }, 'cacheProducts');
  }

  /**
   * Remove specific product IDs from the offline cache (used when backend reports deletions).
   */
  async deleteProductsFromCache(productIds: string[], businessId: string, locationId: string): Promise<void> {
    if (!productIds.length) return;
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('products', 'readwrite');
      const store = tx.objectStore('products');
      await Promise.all(
        productIds.map(productId =>
          store.delete(this.getProductCacheId(businessId, locationId, productId))
        )
      );
      await tx.done;
    }, 'deleteProductsFromCache');
  }

  /**
   * Get the timestamp of the last successful delta sync (for the ?since= query param).
   */
  async getLastDeltaSyncTimestamp(businessId: string, locationId: string): Promise<string | null> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const key = `deltaSyncTimestamp:${businessId}:${locationId}`;
      const entry = await db.get('settings', key);
      return entry ? (entry as any).value : null;
    }, 'getLastDeltaSyncTimestamp');
  }

  /**
   * Persist the timestamp of the last successful delta sync.
   */
  async setLastDeltaSyncTimestamp(businessId: string, locationId: string, timestamp: string): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const key = `deltaSyncTimestamp:${businessId}:${locationId}`;
      await db.put('settings', { key, value: timestamp } as any);
    }, 'setLastDeltaSyncTimestamp');
  }

  private getProductCacheId(businessId: string, locationId: string, productId: string): string {
    return `${businessId}:${locationId}:${productId}`;
  }

  // ============ TAX GROUPS CACHE ============

  async cacheTaxGroups(businessId: string, groups: TaxGroupCacheData['groups']): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const key = `taxGroups:${businessId}`;
      await db.put('taxGroups', {
        id: key,
        businessId,
        data: { groups },
        lastSynced: Date.now(),
      });
    }, 'cacheTaxGroups');
  }

  async getCachedTaxGroups(businessId: string): Promise<TaxGroupCacheData['groups'] | null> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const key = `taxGroups:${businessId}`;
      const entry = await db.get('taxGroups', key);
      return entry?.data?.groups ?? null;
    }, 'getCachedTaxGroups');
  }

  // ============ CUSTOMERS CACHE ============

  async cacheCustomers(businessId: string, customers: any[]): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const now = Date.now();
      const tx = db.transaction('customers', 'readwrite');

      const existingIds = new Set<string>();
      for (const customer of customers) {
        const key = `${businessId}:${customer.id}`;
        existingIds.add(key);
        const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim() || customer.name || '';
        await tx.store.put({
          id: key,
          businessId,
          data: customer,
          lastSynced: now,
          name: name.toLowerCase(),
          email: (customer.email || '').toLowerCase(),
          phone: customer.phone || '',
        });
      }

      await tx.done;
    }, 'cacheCustomers');
  }

  async getCachedCustomers(businessId: string): Promise<any[]> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const index = db.transaction('customers', 'readonly').store.index('by-businessId');
      const entries = await index.getAll(businessId);
      return entries.map((e) => e.data);
    }, 'getCachedCustomers');
  }

  async searchCachedCustomers(businessId: string, query: string): Promise<any[]> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const index = db.transaction('customers', 'readonly').store.index('by-businessId');
      const entries = await index.getAll(businessId);
      const q = query.trim().toLowerCase();
      return entries
        .filter((e) => {
          if (!q) return true;
          return (
            e.name.includes(q) ||
            e.email.includes(q) ||
            e.phone.includes(q)
          );
        })
        .map((e) => e.data);
    }, 'searchCachedCustomers');
  }

  // ============ SYNC SESSIONS METHODS ============

  async saveSyncSession(session: SyncSession): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      await db.put('syncSessions', {
        ...session,
        lastSyncAt: session.lastSyncAt || Date.now()
      });
    }, 'saveSyncSession');
  }

  async getSyncSession(businessId: string): Promise<SyncSession | undefined> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('syncSessions', 'readonly');
      const store = tx.objectStore('syncSessions');
      const index = store.index('by-businessId');

      const sessions = await index.getAll(businessId);
      return sessions.length > 0 ? sessions[0] : undefined;
    }, 'getSyncSession');
  }

  async deleteSyncSession(id: string): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      await db.delete('syncSessions', id);
    }, 'deleteSyncSession');
  }

  // ============ CONFLICT MANAGEMENT METHODS ============

  async addConflict(conflict: ConflictRecord): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      await db.put('conflicts', {
        ...conflict,
        timestamp: conflict.timestamp || Date.now(),
        status: conflict.status || 'PENDING',
        resolution: conflict.resolution || 'PENDING'
      });
    }, 'addConflict');
  }

  async getConflicts(
    businessId: string,
    options: { status?: string; entityType?: string } = {}
  ): Promise<ConflictRecord[]> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('conflicts', 'readonly');
      const store = tx.objectStore('conflicts');
      const index = store.index('by-businessId');

      let conflicts = await index.getAll(businessId);

      if (options.status) {
        conflicts = conflicts.filter(c => c.status === options.status);
      }
      if (options.entityType) {
        conflicts = conflicts.filter(c => c.entityType === options.entityType);
      }

      return conflicts.sort((a, b) => b.timestamp - a.timestamp);
    }, 'getConflicts');
  }

  async resolveConflict(
    id: string,
    resolution: ConflictRecord['resolution'],
    resolvedBy?: string
  ): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      const tx = db.transaction('conflicts', 'readwrite');
      const store = tx.objectStore('conflicts');
      const existing = await store.get(id);

      if (existing) {
        await store.put({
          ...existing,
          resolution,
          status: 'RESOLVED',
          resolvedAt: Date.now(),
          resolvedBy
        });
      }
      await tx.done;
    }, 'resolveConflict');
  }

  async deleteConflict(id: string): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();
      await db.delete('conflicts', id);
    }, 'deleteConflict');
  }

  // ============ BACKUP & EXPORT FEATURES ============

  async exportData(businessId?: string): Promise<Blob> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();

      const exportData = {
        metadata: {
          timestamp: new Date().toISOString(),
          version: this.dbVersion,
          businessId: businessId || 'all',
          recordCounts: {} as Record<string, number>
        },
        data: {
          pendingTransactions: [] as PendingTransaction[],
          products: [] as CachedProduct[],
          conflicts: [] as ConflictRecord[],
          syncSessions: [] as SyncSession[],
          settings: [] as StorageSettings[]
        }
      };

      // Export data based on businessId filter
      if (businessId) {
        exportData.data.pendingTransactions = await this.getPendingTransactions(businessId);
        const productTx = db.transaction('products', 'readonly');
        exportData.data.products = await productTx.objectStore('products').index('by-businessId').getAll(businessId);
        exportData.data.conflicts = await this.getConflicts(businessId);
        const session = await this.getSyncSession(businessId);
        if (session) exportData.data.syncSessions = [session];
      } else {
        const tx = db.transaction([
          'pendingTransactions',
          'products',
          'conflicts',
          'syncSessions',
          'settings'
        ], 'readonly');

        exportData.data.pendingTransactions = await tx.objectStore('pendingTransactions').getAll();
        exportData.data.products = await tx.objectStore('products').getAll();
        exportData.data.conflicts = await tx.objectStore('conflicts').getAll();
        exportData.data.syncSessions = await tx.objectStore('syncSessions').getAll();
        exportData.data.settings = await tx.objectStore('settings').getAll();
      }

      // Update record counts
      Object.keys(exportData.data).forEach(key => {
        exportData.metadata.recordCounts[key] = (exportData.data as any)[key].length;
      });

      // Update last backup timestamp if backup is enabled
      const settings = await this.getSettings();
      if (settings.backupEnabled) {
        await this.updateSettings({ lastBackupAt: Date.now() });
      }

      return new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
    }, 'exportData');
  }

  async importData(blob: Blob, options: { clearExisting?: boolean; businessId?: string } = {}): Promise<void> {
    return this.withRetry(async () => {
      const data = JSON.parse(await blob.text());

      // Validate import data structure
      if (!data.metadata || !data.data) {
        throw new Error('Invalid backup file format: missing metadata or data');
      }

      if (data.metadata.version > this.dbVersion) {
        throw new Error(`Backup version ${data.metadata.version} is newer than current version ${this.dbVersion}`);
      }

      const db = await this.ensureDb();

      // Clear existing data if requested
      if (options.clearExisting) {
        await this.clearAll(options.businessId);
      }

      const tx = db.transaction([
        'pendingTransactions',
        'products',
        'conflicts',
        'syncSessions',
        'settings'
      ], 'readwrite');

      // Import data with business ID filtering if specified
      const importData = (storeName: 'pendingTransactions' | 'products' | 'conflicts' | 'syncSessions' | 'settings', items: any[]) => {
        if (!items.length) return Promise.resolve();

        const filteredItems = options.businessId
          ? items.filter(item => item.businessId === options.businessId)
          : items;

        return Promise.all(
          filteredItems.map(item => tx.objectStore(storeName).put(item))
        );
      };

      await Promise.all([
        importData('pendingTransactions', data.data.pendingTransactions || []),
        importData('products', data.data.products || []),
        importData('conflicts', data.data.conflicts || []),
        importData('syncSessions', data.data.syncSessions || []),
        ...(data.data.settings ? data.data.settings.map((s: StorageSettings) =>
          tx.objectStore('settings').put(s)
        ) : [])
      ]);

      await tx.done;

    }, 'importData');
  }

  // ============ MAINTENANCE & UTILITY METHODS ============

  async getStorageSize(): Promise<{ used: number; available: number; percentage: number }> {
    if (!navigator.storage || !navigator.storage.estimate) {
      return { used: 0, available: 0, percentage: 0 };
    }

    try {
      const estimate = await navigator.storage.estimate();
      const used = estimate.usage || 0;
      const available = estimate.quota || 0;
      const percentage = available > 0 ? (used / available) * 100 : 0;

      return { used, available, percentage };
    } catch (error) {
      return { used: 0, available: 0, percentage: 0 };
    }
  }

  async clearAll(businessId?: string): Promise<void> {
    return this.withRetry(async () => {
      const db = await this.ensureDb();

      if (businessId) {
        // Clear business-specific data
        await this.clearCompletedTransactions(businessId);
        await this.clearProductCache(businessId);

        const conflicts = await this.getConflicts(businessId);
        await Promise.all(conflicts.map(c => this.deleteConflict(c.id)));

        const session = await this.getSyncSession(businessId);
        if (session) {
          await this.deleteSyncSession(session.id);
        }
      } else {
        // Clear everything
        const tx = db.transaction([
          'pendingTransactions',
          'products',
          'conflicts',
          'syncSessions'
        ], 'readwrite');

        await Promise.all([
          tx.objectStore('pendingTransactions').clear(),
          tx.objectStore('products').clear(),
          tx.objectStore('conflicts').clear(),
          tx.objectStore('syncSessions').clear()
        ]);

        await tx.done;
      }
    }, 'clearAll');
  }

  async performMaintenance(): Promise<{ cleaned: number; errors: number }> {
    const results = { cleaned: 0, errors: 0 };
    const settings = await this.getSettings();

    if (!settings.autoCleanup) {
      return results;
    }

    try {
      // Clean up old completed transactions (older than 7 days)
      const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const db = await this.ensureDb();

      const tx = db.transaction('pendingTransactions', 'readwrite');
      const store = tx.objectStore('pendingTransactions');
      const index = store.index('by-timestamp');

      const oldTransactions = await index.getAll(IDBKeyRange.upperBound(weekAgo));
      const toDelete = oldTransactions.filter(t =>
        t.status === 'COMPLETED' ||
        (t.status === 'FAILED' && t.retryCount >= settings.maxRetryCount)
      );

      await Promise.all(toDelete.map(t => store.delete(t.id)));
      await tx.done;

      results.cleaned += toDelete.length;

      // Clean up expired product cache (older than TTL)
      const productTx = db.transaction('products', 'readwrite');
      const productStore = productTx.objectStore('products');
      const productIndex = productStore.index('by-lastSynced');

      const expiredProducts = await productIndex.getAll(IDBKeyRange.upperBound(Date.now() - settings.cacheTTL));
      const inactiveProducts = expiredProducts.filter(p => !p.localChanges);

      await Promise.all(inactiveProducts.map(p => productStore.delete(p.id)));
      await productTx.done;

      results.cleaned += inactiveProducts.length;

    } catch (error) {
      results.errors++;
    }

    return results;
  }

  // ============ MIGRATION FROM LOCALSTORAGE ============

  async migrateFromLocalStorage(): Promise<{ migrated: number; errors: number }> {
    const results = { migrated: 0, errors: 0 };

    try {
      // Migrate pending transactions
      const pendingStr = localStorage.getItem('pendingTransactions');
      if (pendingStr) {
        const transactions: any[] = JSON.parse(pendingStr);

        for (const tx of transactions) {
          try {
            const pendingTx: PendingTransaction = {
              id: tx.offlineId || tx.id || `migrated-${Date.now()}-${Math.random()}`,
              businessId: tx.businessId || 'unknown',
              transactionData: tx,
              timestamp: new Date(tx.timestamp || tx.createdAt || Date.now()).getTime(),
              status: 'PENDING',
              retryCount: 0,
              type: this.determineTransactionType(tx),
              version: 1,
              priority: 'NORMAL'
            };

            await this.addPendingTransaction(pendingTx);
            results.migrated++;
          } catch (error) {
            results.errors++;
          }
        }

        localStorage.removeItem('pendingTransactions');
      }

      // Migrate sync sessions
      const sessionsStr = localStorage.getItem('activeSyncSessions');
      if (sessionsStr) {
        const sessions: any[] = JSON.parse(sessionsStr);

        for (const session of sessions) {
          try {
            const syncSession: SyncSession = {
              id: session.id || `migrated-session-${Date.now()}`,
              businessId: session.businessId || 'unknown',
              lastSyncAt: session.lastSyncAt ? new Date(session.lastSyncAt).getTime() : null,
              syncInProgress: session.syncInProgress || false,
              conflictCount: session.conflictCount || 0,
              resolvedConflicts: session.resolvedConflicts || 0,
              failedTransactions: session.failedTransactions || 0,
              status: session.status || 'INACTIVE'
            };

            await this.saveSyncSession(syncSession);
            results.migrated++;
          } catch (error) {
            results.errors++;
          }
        }

        localStorage.removeItem('activeSyncSessions');
      }

   } catch (error) {
      results.errors++;
    }

    return results;
  }

  private determineTransactionType(transaction: any): PendingTransaction['type'] {
    if (transaction.type) return transaction.type;
    if (transaction.items) return 'SALE';
    if (transaction.sku || transaction.productId) return 'PRODUCT';
    if (transaction.adjustmentType) return 'INVENTORY';
    return 'CUSTOMER';
  }

  // ============ HEALTH CHECK ============

  async healthCheck(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy';
    details: {
      database: boolean;
      storage: { used: number; available: number; percentage: number };
      performance: PerformanceMetrics;
      settings: StorageSettings;
    };
  }> {
    try {
      const db = await this.ensureDb();
      const storage = await this.getStorageSize();
      const settings = await this.getSettings();
      const performance = this.getPerformanceMetrics();

      // Test basic operations
      await db.count('pendingTransactions');
      await db.count('products');

      const status: 'healthy' | 'degraded' | 'unhealthy' =
        storage.percentage > 90 ? 'unhealthy' :
          performance.errors > 10 ? 'degraded' : 'healthy';

      return {
        status,
        details: {
          database: true,
          storage,
          performance,
          settings
        }
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        details: {
          database: false,
          storage: { used: 0, available: 0, percentage: 0 },
          performance: this.getPerformanceMetrics(),
          settings: this.defaultSettings
        }
      };
    }
  }

  // ============ DESTRUCTOR ============


  // ============ CASH REGISTER CACHING METHODS ============

  async getActiveRegisterSession(businessId?: string): Promise<CachedCashRegisterSession | null> {
    try {
      const db = await this.ensureDb();
      const key = businessId ? ('active_cash_register_' + businessId) : 'active_cash_register_default';
      const record = await db.get('settings', key);
      return (record as any)?.session || null;
    } catch {
      return null;
    }
  }

  async setActiveRegisterSession(session: CachedCashRegisterSession | null, businessId?: string): Promise<void> {
    try {
      const db = await this.ensureDb();
      const key = businessId ? ('active_cash_register_' + businessId) : 'active_cash_register_default';
      if (!session) {
        await db.delete('settings', key);
      } else {
        await db.put('settings', {
          id: key,
          session,
          updatedAt: Date.now(),
        } as any);
      }
    } catch (e) {
      console.warn('Failed to cache register session in offline storage:', e);
    }
  }

  async destroy(): Promise<void> {
    this.closeConnection();
    this.resetPerformanceMetrics();
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();
export default offlineStorage;
